import React, { type ReactNode } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Row } from "./Row";

export interface PageHeaderProps {
  /** Page title (h5). */
  title: string;
  /** Optional one-line description under the title. */
  subtitle?: ReactNode;
  /** When true, render a back affordance that navigates to the previous
   *  route (used by Project / Schedule detail pages). */
  back?: boolean;
  /** Explicit back target; defaults to `navigate(-1)` when `back` is set. */
  backTo?: string;
  /** Right-aligned actions (buttons, chips, refresh). */
  actions?: ReactNode;
}

/**
 * Unified workspace page header. Replaces the five divergent header layouts
 * (h5+subtitle Row vs Back-button Stack) the workspace pages each rolled
 * their own version of, so every page reads the same.
 */
export function PageHeader({ title, subtitle, back, backTo, actions }: PageHeaderProps) {
  const navigate = useNavigate();
  return (
    <Row
      align={subtitle ? "start" : "center"}
      justify="space-between"
      sx={{ mb: 2.5, flexWrap: "wrap", gap: 1.5 }}
    >
      <Row align="center" gap={1.5} sx={{ minWidth: 0 }}>
        {back && (
          <Tooltip title="Back">
            <IconButton
              size="small"
              aria-label="go back"
              onClick={() => (backTo ? navigate(backTo) : navigate(-1))}
              sx={{ mt: subtitle ? 0.25 : 0 }}
            >
              <ArrowLeft size={18} />
            </IconButton>
          </Tooltip>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }} noWrap>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, maxWidth: 640 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Row>
      {actions && (
        <Row align="center" gap={1} sx={{ flexShrink: 0 }}>
          {actions}
        </Row>
      )}
    </Row>
  );
}
